import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useViewportContainers } from './HeroStageContent.config';
import { Background, BackgroundGradient, Container } from './HeroStageContent.styles';
import { HeroStageOverlay } from './HeroStageOverlay';
import { getAnimatedStyle } from './HeroStageOverlay.styles';
import { useInitialRender } from '../../../../play-core/hooks/use-initial-render';
import { useAnalyticsDispatch } from '../../analytics/AnalyticsContext';
import { useUdpSectionTracking } from '../../analytics/use-udp-section-tracking';
import { useHydrated } from '../../hooks/use-hydrated';
import { usePathByMediaType } from '../../hooks/use-path-by-media-type';
import { getMediaAria } from '../../utils/aria-helper';
import { SmartLink } from '../ClickableComponents/SmartLink';
import { PlayImage } from '../Image/PlayImage';
import { useSectionMeta } from '../Section/SectionMetaProvider';
import { Slider } from '../Slider/Slider';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const maxSliderItems = 15;
export const HeroStageContent = ({
  medias
}) => {
  const containers = useViewportContainers();
  // We only want to show a limited number of items in the slider.
  const sliderMedias = medias.slice(0, maxSliderItems);

  // Causes to re-render on client side which is necessary because of our tricks
  // we do with SSR in useViewportContainers().
  const isInitialRender = useInitialRender();
  return /*#__PURE__*/_jsx(Fragment, {
    children: containers.map(({
      aspectRatio,
      width,
      visibleViewport
    }) => /*#__PURE__*/_jsx(Container, {
      visibleViewport: visibleViewport,
      children: /*#__PURE__*/_jsx(Slider, {
        slides: sliderMedias,
        renderSlide: ({
          item,
          ...props
        }) => /*#__PURE__*/_jsx(Slide, {
          width: width,
          aspectRatio: aspectRatio,
          media: item,
          ...props
        })
      })
    }, `${visibleViewport}-${!isInitialRender}`))
  });
};
const Slide = /*#__PURE__*/React.memo(({
  media,
  aspectRatio,
  index,
  width,
  sliderProgress,
  transitionProgress,
  numberOfSlides
}) => {
  const analytics = useAnalyticsDispatch();
  const sectionMeta = useSectionMeta();
  const {
    t
  } = useTranslation();
  const trackSection = useUdpSectionTracking();
  const isHydrated = useHydrated();
  const analyticsIndex = index + 1;
  const style = getAnimatedStyle({
    index,
    transitionProgress,
    sliderProgress,
    numberOfSlides
  });
  return /*#__PURE__*/_jsxs(SmartLink, {
    to: usePathByMediaType(media),
    state: {
      media
    },
    isInternal: true,
    "aria-label": isHydrated ? getMediaAria(media, t) : undefined,
    onClick: () => {
      analytics({
        type: 'click-event',
        name: 'HeroStageClick',
        value1: media.urn,
        value2: sectionMeta.sectionId,
        value3: analyticsIndex.toString(),
        value4: media.title
      });
      if (sectionMeta.representationName) {
        trackSection({
          sectionMeta,
          item: {
            ...media,
            index
          }
        });
      }
    },
    children: [/*#__PURE__*/_jsxs(Background, {
      children: [/*#__PURE__*/_jsx(PlayImage, {
        hasBorderRadius: false,
        aspectRatio: aspectRatio,
        src: media?.imageUrl,
        width: width,
        alt: media?.title,
        priorize: index === 0,
        focalPoint: media.imageFocalPoint
      }), /*#__PURE__*/_jsx(BackgroundGradient, {})]
    }), /*#__PURE__*/_jsx(HeroStageOverlay, {
      style: style,
      media: media,
      aspectRatio: aspectRatio
    })]
  }, media.urn);
});